import React, { useState, useEffect } from 'react';
import { reportsAPI, studentsAPI, feesAPI, attendanceAPI } from '../../services/api';
import { formatCurrency } from '../../utils/helpers';

const ReportsAnalytics = () => {
  const [tab, setTab] = useState('overview');
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('attendance');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    setLoading(true);
    reportsAPI.dashboard().then(r => setSummary(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const reportCards = [
    { icon: '📊', title: 'Academic Performance', desc: 'Class-wise and subject-wise performance analytics', color: '#eff6ff', action: 'Generate' },
    { icon: '💰', title: 'Financial Report', desc: 'Fee collection, pending dues, monthly revenue', color: '#f0fdf4', action: 'Generate' },
    { icon: '✅', title: 'Attendance Summary', desc: 'Daily, weekly, monthly attendance statistics', color: '#faf5ff', action: 'Generate' },
    { icon: '📝', title: 'Exam Results', desc: 'Pass/fail analysis, grade distribution', color: '#fff7ed', action: 'Generate' },
    { icon: '🚌', title: 'Transport Report', desc: 'Vehicle utilization and route analysis', color: '#f0fdfa', action: 'Generate' },
    { icon: '📚', title: 'Library Report', desc: 'Issue/return statistics and overdue books', color: '#fef2f2', action: 'Generate' },
  ];

  return (
    <div>
      <div className="page-header">
        <div><h2>Reports & Analytics</h2><p>Comprehensive insights and custom reports</p></div>
        <button className="btn btn-primary">📥 Export Dashboard</button>
      </div>

      <div className="d-flex gap-2 mb-4 flex-wrap">
        {[['overview','📊 Overview'],['academic','🎓 Academic'],['financial','💰 Financial'],['attendance','✅ Attendance'],['custom','⚙️ Custom']].map(([k,l]) => (
          <button key={k} className={`btn ${tab === k ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div>
          <div className="row g-3 mb-4">
            {[
              { icon: '🎓', label: 'Total Students', value: summary.total_students || 0, bg: '#eff6ff', color: '#1d4ed8' },
              { icon: '👨‍🏫', label: 'Total Staff', value: summary.total_staff || 0, bg: '#f0fdf4', color: '#15803d' },
              { icon: '💰', label: 'Fee Collected (Month)', value: formatCurrency(summary.fee_collected_month || 0), bg: '#faf5ff', color: '#7c3aed' },
              { icon: '⚠️', label: 'Pending Fees', value: summary.pending_fees || 0, bg: '#fff7ed', color: '#c2410c' },
            ].map(s => (
              <div key={s.label} className="col-6 col-lg-3">
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: s.bg, color: s.color, fontSize: 22 }}>{s.icon}</div>
                  <div className="stat-info"><h3>{s.value}</h3><p>{s.label}</p></div>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-3">
            <div className="col-12">
              <div className="card">
                <div className="card-header"><span>📋 Quick Reports</span></div>
                <div className="card-body">
                  <div className="row g-3">
                    {reportCards.map(r => (
                      <div key={r.title} className="col-md-4">
                        <div style={{ background: r.color, borderRadius: 12, padding: 20, height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <div style={{ fontSize: 28, marginBottom: 10 }}>{r.icon}</div>
                          <h6 style={{ fontWeight: 700, marginBottom: 6 }}>{r.title}</h6>
                          <p style={{ fontSize: 12, color: '#64748b', flex: 1 }}>{r.desc}</p>
                          <div className="d-flex gap-2 mt-2">
                            <button className="btn btn-sm btn-primary">📊 {r.action}</button>
                            <button className="btn btn-sm btn-outline-secondary">📥 Download</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'academic' && (
        <div className="card"><div className="card-body">
          <div className="row g-2 mb-4">
            <div className="col-md-3"><label className="form-label">Class</label><select className="form-select"><option>All Classes</option></select></div>
            <div className="col-md-3"><label className="form-label">Exam</label><select className="form-select"><option>Select Exam</option></select></div>
            <div className="col-md-2 d-flex align-items-end"><button className="btn btn-primary w-100">🔍 Generate</button></div>
          </div>
          <div className="empty-state"><div className="empty-icon">📊</div><h5>Academic Performance Report</h5><p>Select filters to generate academic performance report.</p></div>
        </div></div>
      )}

      {tab === 'financial' && (
        <div className="card"><div className="card-body">
          <div className="row g-2 mb-4">
            <div className="col-md-3"><label className="form-label">Month</label><input type="month" className="form-control" /></div>
            <div className="col-md-3"><label className="form-label">Category</label><select className="form-select"><option>All Categories</option></select></div>
            <div className="col-md-2 d-flex align-items-end"><button className="btn btn-primary w-100">🔍 Generate</button></div>
          </div>
          <div className="empty-state"><div className="empty-icon">💰</div><h5>Financial Report</h5><p>Generate detailed financial reports with collection analysis.</p></div>
        </div></div>
      )}

      {tab === 'attendance' && (
        <div className="card"><div className="card-body">
          <div className="row g-2 mb-4">
            <div className="col-md-3"><label className="form-label">From Date</label><input type="date" className="form-control" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
            <div className="col-md-3"><label className="form-label">To Date</label><input type="date" className="form-control" value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
            <div className="col-md-3"><label className="form-label">Class</label><select className="form-select"><option>All Classes</option></select></div>
            <div className="col-md-2 d-flex align-items-end"><button className="btn btn-primary w-100">🔍 Generate</button></div>
          </div>
          <div className="empty-state"><div className="empty-icon">✅</div><h5>Attendance Summary</h5><p>View daily, weekly, and monthly attendance statistics.</p></div>
        </div></div>
      )}

      {tab === 'custom' && (
        <div className="card"><div className="card-body">
          <h5 className="mb-4">⚙️ Custom Report Builder</h5>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Report Type</label>
              <select className="form-select" value={reportType} onChange={e => setReportType(e.target.value)}>
                <option value="attendance">Attendance</option>
                <option value="academic">Academic Performance</option>
                <option value="financial">Financial</option>
                <option value="transport">Transport</option>
              </select>
            </div>
            <div className="col-md-3"><label className="form-label">From Date</label><input type="date" className="form-control" /></div>
            <div className="col-md-3"><label className="form-label">To Date</label><input type="date" className="form-control" /></div>
            <div className="col-12">
              <div className="d-flex gap-2">
                <button className="btn btn-primary">📊 Generate Report</button>
                <button className="btn btn-success">📥 Export PDF</button>
                <button className="btn btn-outline-success">📊 Export Excel</button>
              </div>
            </div>
          </div>
        </div></div>
      )}
    </div>
  );
};
export default ReportsAnalytics;
